/**
 * next-auth/react shim over the hand-rolled auth endpoints (app/server
 * session.ts). Same surface the app uses: SessionProvider + useSession
 * ({ data, status }), signIn("google", { callbackUrl }), signOut. The
 * session fetch goes through /api/auth/session, which the SW serves
 * StaleWhileRevalidate — so the offline auth-gate behavior (lesson 6) is
 * unchanged: cached session answers while offline, and the gate never
 * bounces a user with cached data.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { maybeSilentReauth } from "@/lib/silent-reauth";

export interface Session {
  user?: { email?: string; name?: string };
  /** When the session cookie was minted (epoch ms); see lib/silent-reauth.ts. */
  issuedAt?: number;
  /** Server back-off after a failed silent re-auth on this device (epoch ms). */
  reauthSkipUntil?: number;
}
type Status = "loading" | "authenticated" | "unauthenticated";

const Ctx = createContext<{ data: Session | null; status: Status }>({ data: null, status: "loading" });

export function SessionProvider({ children }: { children: ReactNode; session?: Session | null }) {
  const [state, setState] = useState<{ data: Session | null; status: Status }>({ data: null, status: "loading" });
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/session")
      .then((r) => (r.ok ? r.json() : {}))
      .then((s: Session) => {
        if (!alive) return;
        if (s?.user?.email) {
          // An old session takes the silent Google hop (prompt=none) so the
          // OAuth client stays in use — a sub-second round trip back to this
          // page; the page still renders meanwhile. See lib/silent-reauth.ts.
          maybeSilentReauth(s);
          setState({ data: s, status: "authenticated" });
        } else setState({ data: null, status: "unauthenticated" });
      })
      .catch(() => {
        // Offline with no cached session: report unauthenticated — the app's
        // gates already refuse to redirect while offline with cached data.
        if (alive) setState({ data: null, status: "unauthenticated" });
      });
    return () => {
      alive = false;
    };
  }, []);
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useSession() {
  return useContext(Ctx);
}

export function signIn(_provider?: string, opts?: { callbackUrl?: string }) {
  const cb = opts?.callbackUrl ?? window.location.pathname + window.location.search;
  window.location.href = `/api/auth/signin?callbackUrl=${encodeURIComponent(cb)}`;
}

export function signOut(opts?: { callbackUrl?: string }) {
  // The server clears the cookie and redirects to /login; a custom
  // callbackUrl rides along for parity with next-auth's signature.
  const cb = opts?.callbackUrl ? `?callbackUrl=${encodeURIComponent(opts.callbackUrl)}` : "";
  window.location.href = `/api/auth/signout${cb}`;
}
